import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import MuiTooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {ArcElement, Chart as ChartJS, Tooltip} from 'chart.js';
import {Doughnut} from 'react-chartjs-2';
import {MinimalCutSet} from '../../../../utils/minimal-cut-sets';
import {AdjustedRiskProbabilities, computeInherentRiskScore, DEFAULT_RISK_WEIGHTS, InherentRiskScore, RiskWeights} from '../../../../utils/inherent-risk-score';

ChartJS.register(ArcElement, Tooltip);

interface InherentRiskGaugeProps {
    cutSets: MinimalCutSet[];
    totalProbability: number;
    totalBaseEvents: number;
    structuralHubIds: Set<number>;
    chartRef?: React.RefObject<ChartJS | null>;
    adjustedProbabilities?: AdjustedRiskProbabilities | null;
    onWeightsChange?: (weights: RiskWeights) => void;
    topNodeUniqueId?: string;
    topNodeName?: string;
}

const ZONE_COLORS = ['#4caf50', '#f9a825', '#e65100', '#d32f2f'];
const ZONE_LABELS = ['Low', 'Moderate', 'High', 'Critical'];

function formatProb(value: number): string {
    if (value === 0) return '0';
    if (value < 0.001) return value.toExponential(2);
    return value.toPrecision(3);
}

const needlePlugin = {
    id: 'gaugeNeedle',
    afterDraw(chart: ChartJS): void {
        const meta = chart.getDatasetMeta(0);
        if (!meta || meta.data.length === 0) return;

        // Get the score from chart config
        const score = (chart.config.options as Record<string, unknown>)?.gaugeScore as number | undefined;
        const riskZone = (chart.config.options as Record<string, unknown>)?.gaugeZone as string | undefined;
        const riskColor = (chart.config.options as Record<string, unknown>)?.gaugeColor as string | undefined;
        const probText = (chart.config.options as Record<string, unknown>)?.gaugeProbText as string | undefined;
        const ghostScore = (chart.config.options as Record<string, unknown>)?.gaugeGhostScore as number | undefined;
        const ghostColor = (chart.config.options as Record<string, unknown>)?.gaugeGhostColor as string | undefined;
        if (score === undefined) return;

        const {ctx} = chart;
        const arc = meta.data[0] as unknown as {x: number; y: number; outerRadius: number; innerRadius: number};
        const cx = arc.x;
        const cy = arc.y;
        const needleRadius = arc.outerRadius - 4;

        // Needle angle: 0=left (π), 100=right (2π)
        const angle = Math.PI + (score / 100) * Math.PI;
        const tipX = cx + Math.cos(angle) * needleRadius;
        const tipY = cy + Math.sin(angle) * needleRadius;

        // Draw ghost needle (sensitivity explorer adjustment)
        if (ghostScore !== undefined) {
            const ghostAngle = Math.PI + (ghostScore / 100) * Math.PI;
            const ghostTipX = cx + Math.cos(ghostAngle) * needleRadius;
            const ghostTipY = cy + Math.sin(ghostAngle) * needleRadius;
            ctx.save();
            ctx.globalAlpha = 0.35;
            ctx.beginPath();
            ctx.moveTo(ghostTipX, ghostTipY);
            const gb1 = ghostAngle + Math.PI / 2;
            const gb2 = ghostAngle - Math.PI / 2;
            ctx.lineTo(cx + Math.cos(gb1) * 3, cy + Math.sin(gb1) * 3);
            ctx.lineTo(cx + Math.cos(gb2) * 3, cy + Math.sin(gb2) * 3);
            ctx.closePath();
            ctx.fillStyle = ghostColor || '#1565c0';
            ctx.fill();
            ctx.restore();

            // Ghost score label just beyond the needle tip
            ctx.save();
            const labelAngle = ghostAngle;
            const labelR = needleRadius + 10;
            const lx = cx + Math.cos(labelAngle) * labelR;
            const ly = cy + Math.sin(labelAngle) * labelR;
            ctx.font = 'bold 9px Roboto, sans-serif';
            ctx.fillStyle = ghostColor || '#1565c0';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(Math.round(ghostScore).toString(), lx, ly);
            ctx.restore();
        }

        // Draw needle
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(tipX, tipY);
        const baseAngle1 = angle + Math.PI / 2;
        const baseAngle2 = angle - Math.PI / 2;
        const baseLen = 4;
        ctx.lineTo(cx + Math.cos(baseAngle1) * baseLen, cy + Math.sin(baseAngle1) * baseLen);
        ctx.lineTo(cx + Math.cos(baseAngle2) * baseLen, cy + Math.sin(baseAngle2) * baseLen);
        ctx.closePath();
        ctx.fillStyle = '#333';
        ctx.fill();

        // Pivot circle
        ctx.beginPath();
        ctx.arc(cx, cy, 6, 0, Math.PI * 2);
        ctx.fillStyle = '#333';
        ctx.fill();

        // Score text
        ctx.textAlign = 'center';
        ctx.font = 'bold 28px Roboto, sans-serif';
        ctx.fillStyle = riskColor || '#333';
        ctx.fillText(Math.round(score).toString(), cx, cy - 18);

        // Zone label
        ctx.font = 'bold 12px Roboto, sans-serif';
        ctx.fillStyle = riskColor || '#333';
        ctx.fillText(riskZone || '', cx, cy - 4);

        // P(top) text
        ctx.font = '10px Roboto, sans-serif';
        ctx.fillStyle = '#666';
        ctx.fillText(probText || '', cx, cy + 24);

        ctx.restore();
    },
};

const SUB_SCORE_DEFS: Array<{key: keyof InherentRiskScore; label: string; color: string; tooltip: (value: number) => string}> = [
    {key: 'logScore', label: 'P(top)', color: '#1976d2',
        tooltip: (v) => `Top event probability mapped on a logarithmic scale (1e-11 = 0, 0.1 = 100). Current score: ${Math.round(v)}/100.`},
    {key: 'spfPenalty', label: 'SPF Risk Exposure', color: '#d32f2f',
        tooltip: (v) => `${Math.round(v)}% of total risk comes from single points of failure (order-1 cut sets) with no redundancy protection.`},
    {key: 'hubPenalty', label: 'Hub Risk Exposure', color: '#ff9800',
        tooltip: (v) => `${Math.round(v)}% of total risk flows through cut sets containing structural hubs — events appearing in 50%+ of all cut sets.`},
    {key: 'orderConcentration', label: 'Low-Order Conc.', color: '#7b1fa2',
        tooltip: (v) => `${Math.round(v)}% of total risk is concentrated in low-order cut sets (order 1–3), which require fewer simultaneous failures.`},
];

type WeightKey = keyof RiskWeights;
const WEIGHT_KEYS: WeightKey[] = ['logScore', 'spfPenalty', 'hubPenalty', 'orderConcentration'];
const WEIGHT_LABELS: Record<WeightKey, string> = {
    logScore: 'P(top)',
    spfPenalty: 'SPF Risk Exposure',
    hubPenalty: 'Hub Risk Exposure',
    orderConcentration: 'Low-Order Conc.',
};

const InherentRiskGauge: React.FC<InherentRiskGaugeProps> = ({cutSets, totalProbability, totalBaseEvents, structuralHubIds, chartRef, adjustedProbabilities, onWeightsChange, topNodeUniqueId, topNodeName}) => {
    const [weights, setWeights] = useState<RiskWeights>({...DEFAULT_RISK_WEIGHTS});

    const riskScore = useMemo((): InherentRiskScore => {
        return computeInherentRiskScore(cutSets, totalProbability, totalBaseEvents, structuralHubIds, weights);
    }, [cutSets, totalProbability, totalBaseEvents, structuralHubIds, weights]);

    const adjustedRiskScore = useMemo((): InherentRiskScore | null => {
        if (adjustedProbabilities == null) return null;
        return computeInherentRiskScore(cutSets, adjustedProbabilities.totalProbability, totalBaseEvents, structuralHubIds, weights, {
            order1Probability: adjustedProbabilities.order1Probability,
            hubProbability: adjustedProbabilities.hubProbability,
            order1to3Probability: adjustedProbabilities.order1to3Probability,
        });
    }, [cutSets, adjustedProbabilities, totalBaseEvents, structuralHubIds, weights]);

    const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);

    const totalWeight = useMemo((): number => {
        return WEIGHT_KEYS.reduce((sum, k) => sum + weights[k], 0);
    }, [weights]);

    // Debounce onWeightsChange to avoid re-rendering the heavy parent on every slider tick
    const weightsDebounceRef = useRef<ReturnType<typeof setTimeout>>();
    useEffect(() => {
        if (!onWeightsChange) return;
        clearTimeout(weightsDebounceRef.current);
        weightsDebounceRef.current = setTimeout(() => {
            onWeightsChange(weights);
        }, 150);
        return (): void => clearTimeout(weightsDebounceRef.current);
    }, [weights, onWeightsChange]);

    const handleWeightChange = useCallback((key: WeightKey, newValue: number): void => {
        setWeights((prev) => ({...prev, [key]: newValue}));
    }, []);

    const handleResetWeights = useCallback((): void => {
        setWeights({...DEFAULT_RISK_WEIGHTS});
    }, []);

    useMemo((): string => {
        let maxContribution = 0;
        let dominantLabel = '';
        for (const def of SUB_SCORE_DEFS) {
            const value = riskScore[def.key] as number;
            const weight = weights[def.key as keyof RiskWeights];
            const contribution = weight * value;
            if (contribution > maxContribution) {
                maxContribution = contribution;
                dominantLabel = def.label;
            }
        }
        const pct = riskScore.composite > 0 ? (maxContribution / riskScore.composite) * 100 : 0;
        return `Risk is primarily driven by ${dominantLabel} — ${Math.round(pct)}% of composite score`;
    }, [riskScore, weights]);

    if (cutSets.length === 0) return null;

    const chartData = {
        labels: ZONE_LABELS,
        datasets: [{
            data: [25, 25, 25, 25],
            backgroundColor: ZONE_COLORS,
            borderWidth: 0,
        }],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        rotation: 270 as const,
        circumference: 180 as const,
        cutout: '70%',
        layout: {padding: {top: 22, left: 16, right: 16, bottom: 32}},
        plugins: {
            tooltip: {enabled: false},
            legend: {display: false},
        },
        // Custom data passed to the needle plugin
        gaugeScore: riskScore.composite,
        gaugeZone: riskScore.riskZone,
        gaugeColor: riskScore.riskColor,
        gaugeProbText: `P(top) = ${formatProb(totalProbability)}`,
        gaugeGhostScore: adjustedRiskScore?.composite,
        gaugeGhostColor: adjustedRiskScore ? adjustedRiskScore.riskColor : undefined,
    };

    return (
        <Box sx={{borderBottom: 1, borderColor: 'divider', pb: 2, mb: 1}}>
            <Box sx={{
                mx: 2, mt: 1, p: 2, borderRadius: 2,
                bgcolor: 'background.paper',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)',
                border: '1px solid',
                borderColor: 'divider',
            }}>
                <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                Structural Vulnerability
                </Typography>
                {(topNodeUniqueId || topNodeName) && (
                    <Typography variant="caption" sx={{color: 'text.secondary', fontSize: '0.7rem', mb: 1, display: 'block'}}>
                        {topNodeUniqueId}{topNodeUniqueId && topNodeName ? ' — ' : ''}{topNodeName}
                    </Typography>
                )}
                <Stack direction="row" alignItems="start" justifyContent="center" spacing={5}>
                    {/* Gauge */}
                    <Box sx={{width: 400}}>
                        <Typography variant="caption" sx={{textAlign: 'center', display: 'block', fontWeight: 600, fontSize: '0.8rem', color: 'text.primary'}}>
                        Vulnerability Score
                        </Typography>
                        <Box sx={{height: 195, position: 'relative'}}>
                            <Doughnut
                                ref={chartRef as React.RefObject<ChartJS<'doughnut'>>}
                                data={chartData}
                                options={chartOptions}
                                plugins={[needlePlugin]}
                            />
                            {/* Clickable overlay on score area */}
                            <Box
                                onClick={(e): void => setPopoverAnchor(e.currentTarget)}
                                sx={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: 80,
                                    height: 50,
                                    cursor: 'pointer',
                                    borderRadius: 1,
                                    '&:hover': {bgcolor: 'rgba(0,0,0,0.04)'},
                                }}
                            />
                        </Box>
                        <Popover
                            open={Boolean(popoverAnchor)}
                            anchorEl={popoverAnchor}
                            onClose={(): void => setPopoverAnchor(null)}
                            anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                            transformOrigin={{vertical: 'top', horizontal: 'center'}}
                        >
                            <Box sx={{p: 2, width: 380}}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 1}}>
                                    <Typography variant="subtitle2">Score Breakdown</Typography>
                                    <Button size="small" onClick={handleResetWeights} sx={{fontSize: '0.65rem', textTransform: 'none', minWidth: 0, px: 1}}>
                                    Reset weights
                                    </Button>
                                </Stack>
                                {totalWeight > 0 && Math.abs(totalWeight - 1) > 0.01 && (
                                    <Typography variant="caption" sx={{color: 'warning.main', fontSize: '0.65rem', display: 'block', mb: 1}}>
                                    Weights sum to {(totalWeight * 100).toFixed(0)}% (should be 100%)
                                    </Typography>
                                )}
                                {WEIGHT_KEYS.map((key) => {
                                    const weight = weights[key];
                                    const value = riskScore[key as keyof InherentRiskScore] as number;
                                    const contribution = weight * value;
                                    return (
                                        <Stack key={key} spacing={0} sx={{mb: 1}}>
                                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                                                <Typography variant="caption" sx={{fontSize: '0.7rem', fontWeight: 500}}>
                                                    {WEIGHT_LABELS[key]}
                                                </Typography>
                                                <Typography variant="caption" sx={{fontSize: '0.7rem', color: 'text.secondary'}}>
                                                    {(weight * 100).toFixed(0)}% x {value.toFixed(1)} = <b>{contribution.toFixed(1)}</b>
                                                </Typography>
                                            </Stack>
                                            <Slider
                                                size="small"
                                                value={weight}
                                                min={0}
                                                max={1}
                                                step={0.05}
                                                onChange={(_, v): void => handleWeightChange(key, v as number)}
                                                sx={{py: 0.5}}
                                            />
                                        </Stack>
                                    );
                                })}
                                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mt: 1, pt: 1, borderTop: 1, borderColor: 'divider'}}>
                                    <Typography variant="caption" sx={{fontWeight: 700, fontSize: '0.75rem'}}>
                                    Composite
                                    </Typography>
                                    <Typography variant="caption" sx={{fontWeight: 700, fontSize: '0.75rem', color: riskScore.riskColor}}>
                                        {riskScore.composite.toFixed(1)}
                                    </Typography>
                                </Stack>
                            </Box>
                        </Popover>
                        <Stack direction="row" justifyContent="space-between" sx={{mt: 0.5, px: 0.5}}>
                            {(['Low', 'Moderate', 'High', 'Critical'] as const).map((label, i) => (
                                <Typography key={label} variant="caption" sx={{
                                    fontSize: '0.6rem',
                                    color: ZONE_COLORS[i],
                                    fontWeight: 600,
                                    textAlign: 'center',
                                    lineHeight: 1.2,
                                    whiteSpace: 'pre-line',
                                    flex: 1,
                                }}>
                                    {label}
                                </Typography>
                            ))}
                        </Stack>

                    </Box>

                    {/* Sub-score breakdown */}
                    <Stack spacing={1} sx={{flex: 1, maxWidth: 360, justifyContent: 'center', paddingTop: 10}}>
                        {SUB_SCORE_DEFS.map(({key, label, color, tooltip}) => {
                            const value = riskScore[key] as number;
                            const adjustedValue = adjustedRiskScore ? adjustedRiskScore[key] as number : null;
                            const showMarker = adjustedValue !== null && Math.round(adjustedValue) !== Math.round(value);
                            const compositeWorsened = adjustedRiskScore ? adjustedRiskScore.composite > riskScore.composite : false;
                            return (
                                <MuiTooltip key={key} title={tooltip(value)} arrow placement="left">
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{cursor: 'help'}}>
                                        <Typography variant="caption" sx={{width: 110, flexShrink: 0, fontSize: '0.7rem'}}>
                                            {label}
                                        </Typography>
                                        <Box sx={{flex: 1, height: 8, bgcolor: 'grey.200', borderRadius: 1, position: 'relative', overflow: 'visible'}}>
                                            <Box sx={{
                                                width: `${Math.min(value, 100)}%`,
                                                height: '100%',
                                                bgcolor: color,
                                                borderRadius: 1,
                                                transition: 'width 0.3s ease',
                                            }} />
                                            {showMarker && (
                                                <Box sx={{
                                                    position: 'absolute',
                                                    left: `${Math.min(adjustedValue, 100)}%`,
                                                    top: -2,
                                                    width: 2,
                                                    height: 12,
                                                    bgcolor: compositeWorsened ? '#d32f2f' : '#4caf50',
                                                    borderRadius: 0.5,
                                                    transition: 'left 0.3s ease',
                                                }} />
                                            )}
                                        </Box>
                                        <Typography variant="caption" sx={{width: 36, textAlign: 'right', fontSize: '0.7rem', fontWeight: 600}}>
                                            {Math.round(value)}
                                        </Typography>
                                    </Stack>
                                </MuiTooltip>
                            );
                        })}
                        {/**
                    <Typography variant="caption" sx={{fontSize: '0.65rem', color: 'text.secondary', fontStyle: 'italic', mt: 0.5}}>
                        {riskSummary}
                    </Typography>
                     */}
                    </Stack>
                </Stack>
            </Box>
        </Box>
    );
};

export default InherentRiskGauge;
